import { useDebouncedHandler, useStableCallback } from "@fidelix/fx-miranda";
import { useEffect, useState } from "react";
import { sidePanelWidth } from "../common/page-layout";

type WindowDimensions = {
  clientWidth: number;
  clientHeight: number;
  innerWidth: number;
  innerHeight: number;
  outerWidth: number;
  outerHeight: number;
  sidePanelWidth: number;
};

/**
 * A hook to listen to window resize events with a debounced callback.
 */
export function useWindowResizeEvent(debounceMs: number = 250) {
  const [dimensions, setDimensions] = useState<WindowDimensions>({
    clientWidth: document.documentElement.clientWidth,
    clientHeight: document.documentElement.clientHeight,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
    sidePanelWidth: sidePanelWidth,
  });

  const handleResize = () => {
    setDimensions({
      clientWidth: window.innerWidth,
      clientHeight: window.innerHeight,
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      outerWidth: window.outerWidth,
      outerHeight: window.outerHeight,
      sidePanelWidth: sidePanelWidth,
    });
  };

  const debouncedHandler = useDebouncedHandler<UIEvent>(
    handleResize,
    debounceMs,
  );

  useEffect(() => {
    window.addEventListener("resize", debouncedHandler);
    return () => window.removeEventListener("resize", debouncedHandler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- debounced callback is stable

  return dimensions;
}

export function useKeyPressed(key: string): boolean {
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    const downHandler = (event: KeyboardEvent) => {
      if (event.key === key) setPressed(true);
    };

    const upHandler = (event: KeyboardEvent) => {
      if (event.key === key) setPressed(false);
    };

    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);

    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
    };
  }, [key]);

  return pressed;
}

export function useKeyPressEvent(
  key: string,
  handler: (event: KeyboardEvent) => void,
) {
  const stableHandler = useStableCallback(handler);

  useEffect(() => {
    const eventHandler = (event: KeyboardEvent) => {
      if (event.key === key) stableHandler(event);
    };

    window.addEventListener("keydown", eventHandler);

    return () => window.removeEventListener("keydown", eventHandler);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps -- stable handler
}

export function usePageVisibility(): boolean {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const eventHandler = () => {
      setIsVisible(!document.hidden);
    };
    document.addEventListener("visibilitychange", eventHandler);

    return () => document.removeEventListener("visibilitychange", eventHandler);
  }, []);

  return isVisible;
}
